import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { Editor } from '@hugerte/hugerte-react';
import { Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import Icon from "@mdi/react";
import { mdiAlertCircleOutline, mdiClose } from "@mdi/js";
import FormDetailProgress from "./FormDetailProgress";
import FormDetailAvis from "./FormDetailAvis";
import FormDetailDecision from "./FormDetailDecision";
import toast from "react-hot-toast";
import axios from "axios";
import Styles from "./FormDetail.scss";

const FormDetail = (props) => {
  const editorRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stateProgress, setStateProgress] = useState(1);

  let backgroundColorTheme = "#454756";
  let baseColorTheme = "#FFFFFF";

  useEffect(() => {
    backgroundColorTheme =
      props.uiTheme && props.uiTheme === "light" ? "#FFFFFF" : "#454756";
    baseColorTheme =
      props.uiTheme && props.uiTheme === "light" ? "#454756" : "#FFFFFF";
    getFormDetailData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.uiTheme, props.centerPageArg?.formDataId]);

  const getFormDetailData = async () => {
    setLoading(true);
    try {
      const result = await axios.post(
        `/Api/Form_data/detail/${props.centerPageArg?.formDataId}`,
        {
          id: props.centerPageArg?.formDataId,
        }
      );

      if (result.data && result.data.formRefData) {
        let formData = { ...result.data.formRefData };

        let htmlContent = formData.form_data?.html_content;

        if (htmlContent) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(htmlContent, "text/html");

          // Replace content inside elements without altering markup
          if (formData.code) {
            doc.body.innerHTML = doc.body.innerHTML.replace("0000000", formData.code);
          }
          if (formData.dem_name) {
            const acquirNameElem = doc.getElementById("acquir_name");
            if (acquirNameElem) acquirNameElem.textContent = formData.dem_name;
          }
          if (formData.terr_addr) {
            const addressElem = doc.getElementById("address_bien");
            if (addressElem) addressElem.textContent = formData.terr_addr;
          }
          if (formData.terr_process) {
            const processElem = doc.getElementById("form_process");
            if (processElem) processElem.textContent = formData.terr_process;
          }

          // Serialize the updated document back to HTML
          formData.form_data.html_content = doc.body.innerHTML;
        }

        setData(formData);
        props.setCurrentDetailForm(formData);
      }
    } catch (error) {
      console.error("Error fetching form details:", error);
      toast.error("Erreur lors de la récupération des détails du formulaire.", { id: "fetch-error" });
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const showToast = (msg = "Actualisation...") => {
    Swal.fire({
      title: `<div style="color:${baseColorTheme};">${msg}</div>`,
      background: backgroundColorTheme,
      toast: true,
    });
    Swal.showLoading();
  };

  const handleUpdateData = async (e, edata) => {
    if (stateProgress === 0) {
      if (!data.dem_name || !data.dem_addr) {
        toast.error("Les champs nom, adresse du demandeur sont requis !", { id: "error" });
        return;
      }
      // Retrieve the editor content at the time of saving
      const editContent = editorRef.current ? editorRef.current.getContent() : "";

      showToast();
      const updatedData = {
        ...data,
        terr_code_all: data.terr_code_all ? data.terr_code_all : data.terr_code_base,
      };
      try {
        await axios.post(`/Api/Form_ref/detail/${e}/update`, updatedData);
        await axios.post(`/Api/Form_data/detail/${edata}/update`, {
          html_content: editContent,
        });
        setStateProgress(1);
        toast.success("Données enregistrées avec succès.", { id: "save-success" });
      } catch (error) {
        Swal.close();
        Swal.fire("Désolé ! Une erreur s'est produite lors de l'enregistrement de vos données.");
        console.error(error);
      }
      Swal.close();
    } else if (stateProgress === 1) {
      setStateProgress(2);
    } else if (stateProgress === 2) {
      handleSavePdf(data.form_data.id); // save pdf silently
      toast.custom(
        <div className={"ue-toast-modal-message"}>
          <h5 className={"text-lg"}>Vos données ont bien été prises en compte.</h5>
          <div
            className={"btn btn-outline-primary btn-lg btn-block py-2"}
            onClick={() => {
              props.setCurrentCenterPage("dashboard");
              toast.dismiss();
            }}
          >
            Retourner à la liste des documents
          </div>
        </div>,
        {
          duration: 8000, // Adjusted duration for better UX
          position: "bottom-center",
          style: {
            marginBottom: "100px",
          },
        }
      );
    }
  };

  const handleValidation = async (e) => {
    showToast();
    try {
      const resultFormRef = await axios.post(`/Api/Form_ref/detail/${e}/toggle_valid`);
      props.setCurrentDetailForm({ ...data, dem_state: resultFormRef.data });
      toast.success("Validation mise à jour avec succès.", { id: "validation-success" });
    } catch (error) {
      console.error("Validation error:", error);
      toast.error("Erreur lors de la validation.", { id: "validation-error" });
    } finally {
      Swal.close();
    }
  };

  const handleSavePdf = (id) => {
    const editContent = editorRef.current ? editorRef.current.getContent() : "";
    if (props.currentDetailForm?.form_data) {
      axios
        .post(
          `/dashboard/formdata/${props.currentDetailForm.form_data.id}/pdf`,
          {
            id: props.currentDetailForm.id,
            eu_editcontent: editContent,
          },
          {
            responseType: "blob",
          }
        )
        .then((response) => {
          // PDF saved successfully
          toast.success("PDF enregistré avec succès.", { id: "pdf-save-success" });
        })
        .catch((error) => {
          console.error("Error saving PDF:", error);
          toast.error("Erreur lors de la sauvegarde du PDF.", { id: "pdf-save-error" });
        });
    }
  };

  const handleSaveAndViewPdf = (id) => {
    const editContent = editorRef.current ? editorRef.current.getContent() : "";
    if (props?.currentDetailForm?.form_state?.doc_link) {
      window.open(props?.currentDetailForm?.form_state?.doc_link);
    } else {
      axios
        .post(
          `/dashboard/formdata/${props?.currentDetailForm?.form_data?.id}/pdf`,
          {
            id: props?.currentDetailForm?.form_data.id,
            eu_editcontent: editContent,
          },
          {
            responseType: "blob",
          }
        )
        .then((response) => {
          const file = new Blob([response.data], { type: "application/pdf" });
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL);
        })
        .catch((error) => {
          console.error("Error viewing PDF:", error);
          toast.error("Erreur lors de la visualisation du PDF.", { id: "pdf-view-error" });
        });
    }
  };

  const handleDeleteForm = (id) => {
    Swal.fire({
      title: "Êtes-vous sûr ?",
      text: "Toutes les données de ce formulaire seront supprimées !",
      showCancelButton: true,
      confirmButtonColor: "#58D2C3",
      cancelButtonColor: "#676767",
      confirmButtonText: "Oui, supprimer cet élément !",
      cancelButtonText: "Annuler",
      toast: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const result = await axios.delete(`/Api/Form_ref/detail/${id}`);
          if (result.data?.formRef) {
            backToDashboard();
          } else {
            toast.error(
              "Désolé ! Un problème est survenu et vos données n'ont pas été supprimées.",
              { id: "delete-error" }
            );
          }
        } catch (error) {
          console.error("Delete error:", error);
          toast.error("Erreur lors de la suppression du formulaire.", { id: "delete-error" });
        }
      }
    });
  };

  const backToDashboard = () => {
    toast.success("Vos données ont bien été supprimées.", { id: "success" });
    props.setCurrentCenterPage("dashboard");
  };

  return (
    <div className={"eu-form-detail-wrapper d-flex flex-column text-dark"}>
      {loading ? (
        <div className="row justify-content-center align-items-center w-100 h-100">
          <div className="d-flex justify-content-center my-2">
            <Spinner animation="border" size={"md"} />
          </div>
        </div>
      ) : (
        <div className="d-flex flex-column-reverse flex-md-row justify-content-between align-items-sm-stretch w-100 h-100">
          {/* Editor Section */}
          <div style={{ display: stateProgress === 0 ? "none" : "flex", width: "50%" }}>
            <div className="d-flex flex-column flex-fill justify-content-center align-items-start mr-md-1 mt-2 mt-md-0">
              <div className={"card h-100 ue-card-form-editor"}>
                <div className="card-body position-relative" style={{ "zIndex": "99" }} >
                  {data?.form_data?.html_content ? (
                    <Editor
                      onInit={(evt, editor) => {
                        editorRef.current = editor;
                      }}
                      initialValue={data.form_data.html_content}
                      init={{
                        // Base path for Hugerte assets
                        base_url: 'https://cdn.jsdelivr.net/npm/hugerte@1',
                        suffix: '.min',

                        // French language pack
                        language: 'fr_FR',
                        language_url: 'https://cdn.jsdelivr.net/npm/hugerte@1/langs/fr_FR.js',

                        menubar: 'edit format table',
                        height: '100%',
                        width: '100%',

                        // All desired plugins; paste & print are built-in
                        plugins: [
                          'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'anchor',
                          'searchreplace', 'visualblocks', 'code', 'fullscreen',
                          'insertdatetime', 'media', 'table', 'help', 'wordcount'
                        ],

                        // Main toolbar—table button added
                        toolbar:
                          'save | undo redo | styleselect | bold italic | ' +
                          'alignleft aligncenter alignright alignjustify | ' +
                          'outdent indent | table | code',

                        // When cursor is in a table, this toolbar appears
                        table_toolbar:
                          'tableprops tabledelete | ' +
                          'tableinsertrowbefore tableinsertrowafter tabledeleterow | ' +
                          'tableinsertcolbefore tableinsertcolafter tabledeletecol | ' +
                          'cellprops celldelete',

                        content_style:
                          "body { font-family: Open Sans, Arial, sans-serif; font-size:13px }"
                      }}
                      onEditorChange={(content) => {
                        /* if you need to sync changes: */
                        // setData(prev => ({ ...prev, html_content: content }));
                      }}
                    />

                  ) : (
                    <div className={"text-center"}>
                      <h4 className={"my-3"}>
                        Cet élément de remplacement est affiché car vous ne possédez pas les droits pour
                        éditer le contenu du formulaire
                      </h4>
                    </div>
                  )}
                  {stateProgress !== 1 && (
                    <div className={"ue-editor-obscure"}>
                      <div className={"ue-editor-alert"}>
                        <div className={"ue-editor-alert-icon bg-danger"}>
                          <Icon path={mdiAlertCircleOutline} size={1.3} color={"#FFF"} />
                        </div>
                        <div className={"ue-editor-alert-msg text-danger"}>
                          {stateProgress === 0 && (
                            <div>
                              Formulaire modifiable à l’étape suivante :{" "}
                              <span className={"font-weight-bold"}>Instruction</span>
                            </div>
                          )}
                          {stateProgress === 2 && <div>Formulaire non modifiable à cette étape</div>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Progress and Form Fields Section */}
          <div
            className="d-flex flex-fill flex-column justify-content-between ue-fom-detail-progress-zone h-100"
            style={{ borderRadius: "8px", zIndex: 99 }}
          >
            <div
              className="ue-fom-detail-progress-zone-content" >
              {/* Close Button */}
              <div className={"d-flex w-100 justify-content-end align-items-center"}>
                <button
                  className={"btn d-flex justify-content-center align-items-center"}
                  onClick={() => props.setCurrentCenterPage("dashboard")}
                >
                  <Icon path={mdiClose} size={0.9} color={"#CCC"} />
                </button>
              </div>

              {/* Progress Indicator */}
              <FormDetailProgress progress={stateProgress} />

              {/* Form Fields for State Progress 0 */}
              {stateProgress === 0 && data && (
                <form
                  className={"form square"}
                  id={"form-Data-fields"}
                  onSubmit={(e) => e.preventDefault()}
                >
                  <div className="d-flex flex-column">
                    <div className="font-weight-bold my-3">Références d'enregistrement</div>
                    <input
                      id="terr_code_base"
                      name="terr_code_base"
                      type="hidden"
                      value={data.terr_code_base || ""}
                      onChange={handleFormChange}
                    />

                    <div className="d-flex flex-column">
                      {/* N° de dossier */}
                      <div className="mb-2">
                        <div className="did-floating-label-content">
                          <input
                            className="did-floating-input"
                            type="text"
                            id="code"
                            name="code"
                            placeholder=" "
                            value={data.code || ""}
                            onChange={handleFormChange}
                          />
                          <label className="did-floating-label">N° de dossier</label>
                        </div>
                      </div>

                      {/* Date de dépôt */}
                      <div className="form-group mb-2">
                        <div className="did-floating-label-content">
                          <input
                            className="did-floating-input"
                            type="text"
                            id="dem_date"
                            name="dem_date"
                            placeholder=" "
                            value={data.dem_date || ""}
                            onChange={handleFormChange}
                          />
                          <label className="did-floating-label">Date de dépôt</label>
                        </div>
                      </div>

                      {/* Parcelle */}
                      <div className="form-group mb-2">
                        <div className="did-floating-label-content">
                          <input
                            className="did-floating-input"
                            type="text"
                            id="terr_code_all"
                            name="terr_code_all"
                            placeholder=" "
                            value={data.terr_code_all || ""}
                            onChange={handleFormChange}
                          />
                          <label className="did-floating-label">Parcelle</label>
                        </div>
                      </div>

                      {/* Localisation du bien */}
                      <div className="form-group mb-2">
                        <div className="did-floating-label-content">
                          <input
                            className="did-floating-input"
                            type="text"
                            id="terr_addr"
                            name="terr_addr"
                            placeholder=" "
                            value={data.terr_addr || ""}
                            onChange={handleFormChange}
                          />
                          <label className="did-floating-label">Localisation du bien</label>
                        </div>
                      </div>
                    </div>

                    <div className="font-weight-bold my-3">Demandeur</div>
                    <div className="d-flex flex-column">
                      {/* Demandeur : nom & prénom, société... */}
                      <div className="form-group mb-2">
                        <div className="did-floating-label-content">
                          <input
                            className="did-floating-input"
                            type="text"
                            id="dem_name"
                            name="dem_name"
                            placeholder=" "
                            value={data.dem_name || ""}
                            onChange={handleFormChange}
                          />
                          <label className="did-floating-label">
                            Demandeur : nom & prénom, société...
                          </label>
                        </div>
                      </div>

                      {/* Demandeur adresse */}
                      <div className="form-group mb-2">
                        <div className="did-floating-label-content">
                          <input
                            className="did-floating-input"
                            type="text"
                            id="dem_addr"
                            name="dem_addr"
                            placeholder=" "
                            value={data.dem_addr || ""}
                            onChange={handleFormChange}
                          />
                          <label className="did-floating-label">Demandeur adresse</label>
                        </div>
                      </div>

                      {/* Demandeur Email */}
                      <div className="form-group mb-2">
                        <div className="did-floating-label-content">
                          <input
                            className="did-floating-input"
                            type="email"
                            id="dem_email"
                            name="dem_email"
                            placeholder=" "
                            value={data.dem_email || ""}
                            onChange={handleFormChange}
                          />
                          <label className="did-floating-label">Demandeur Email</label>
                        </div>
                      </div>

                      {/* Projet */}
                      <div className="form-group">
                        <div className="did-floating-label-content">
                          <input
                            className="did-floating-input"
                            type="text"
                            id="projet"
                            name="projet"
                            placeholder=" "
                            style={{ height: "8rem" }}
                            value={data.projet || ""}
                            onChange={handleFormChange}
                          />
                          <label className="did-floating-label">Projet</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              )}

              {/* FormDetailAvis for stateProgress 1 */}
              {stateProgress === 1 && <FormDetailAvis formData={data} />}

              {/* FormDetailDecision for stateProgress 2 */}
              {stateProgress === 2 && <FormDetailDecision formData={data} />}
            </div>

            {/* Action Buttons */}
            <div
              className="mt-2"
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                height: "30px",
              }}
            >
              <div className="d-flex flex-column flex-md-row w-75 mt-2 mt-md-0">
                {/* Supprimer Button */}
                <button
                  className={"btn btn-outline-danger mr-1 mt-2 mt-md-0 btn-block"}
                  style={{ fontWeight: "600" }}
                  onClick={() => handleDeleteForm(props.centerPageArg?.formDataId)}
                >
                  Supprimer
                </button>

                {/* Annuler Button */}
                <button
                  className={"btn btn-outline-primary mr-1 btn-block mt-md-0"}
                  style={{ fontWeight: "600" }}
                  onClick={() =>
                    stateProgress > 1
                      ? setStateProgress(stateProgress - 1)
                      : props.setCurrentCenterPage("dashboard")
                  }
                >
                  Annuler
                </button>

                {/* Conditional Buttons */}
                {data?.form_data?.html_content && (
                  <>
                    {/* Aperçu Button */}
                    {(stateProgress === 1 || stateProgress === 2) && (
                      <button
                        className={"btn btn-outline-primary ml-1 mt-2 mt-md-0 mr-1 btn-block"}
                        style={{ fontWeight: "600" }}
                        onClick={() => handleSaveAndViewPdf(data.form_data.id)}
                      >
                        Aperçu
                      </button>
                    )}

                    {/* Enregistrer Button */}
                    <button
                      className={"btn btn-primary text-light btn-block mt-2 mt-md-0"}
                      style={{ fontWeight: "600" }}
                      onClick={() => handleUpdateData(data.id, data.form_data.id)}
                    >
                      Enregistrer
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    userId: state.MeReducer.id,
    username: state.MeReducer.username,
    role: state.MeReducer.role,
    centerPageArg: state.DashboardReducer.centerPageArg,
    uiTheme: state.AppReducer.uiTheme, // Corrected the typo here
    currentDetailForm: state.FormsReducer.currentDetailForm,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    showCenterSection: (e) => dispatch({ type: "SHOW_CENTER_SECTION", data: e }),
    setCurrentCenterPage: (e) => dispatch({ type: "SET_CURRENT_PAGE", data: e }),
    setCheckForm: (e) => dispatch({ type: "SET_CHECK_FORMS", data: e }),
    setCurrentDetailForm: (e) => dispatch({ type: "SET_CURRENT_DETAIL_FORM", data: e }),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(FormDetail);
